import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { m, useReducedMotion } from "motion/react";
import { BrandHeader } from "../components/BrandHeader";
import { Button } from "../components/Button";
import { ContactForm } from "../components/ContactForm";
import { QuestionScreen } from "../components/QuestionScreen";
import { RestartDialog } from "../components/RestartDialog";
import { SnapshotView } from "../components/SnapshotView";
import { SnapshotActions } from "../components/SnapshotActions";
import { questions } from "../config/questions";
import { trackEvent, viewportCategory } from "../lib/analytics";
import { generateSnapshot, getAnswerLabels } from "../lib/generateSnapshot";
import { downloadSnapshotPdf } from "../lib/snapshotPdf";
import { clearAssessmentState, restoreAssessmentState, saveAssessmentState } from "../lib/sessionStorage";
import type { AssessmentAnswers, FrictionSnapshot, StoredAssessmentState } from "../types/friction-finder";
import { emptyAnswers } from "../types/friction-finder";

type Step = "landing" | "review" | "snapshot" | "confirmation" | number;

function newSessionId() {
  return typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function PublicTool() {
  const restored = useMemo(() => restoreAssessmentState(), []);
  const [step, setStep] = useState<Step>(restored?.currentStep ?? "landing");
  const [answers, setAnswers] = useState<AssessmentAnswers>(restored?.answers ?? emptyAnswers());
  const [assessmentSessionId, setAssessmentSessionId] = useState(restored?.assessmentSessionId ?? "");
  const [reviewReturnState, setReviewReturnState] = useState(restored?.reviewReturnState ?? false);
  const [snapshot, setSnapshot] = useState<FrictionSnapshot | undefined>(restored?.generatedSnapshot);
  const [restartOpen, setRestartOpen] = useState(false);
  const reviewHeading = useRef<HTMLHeadingElement>(null);
  const reduceMotion = useReducedMotion();
  const screenTransition = { duration: reduceMotion ? 0 : 0.18, ease: [0.22, 1, 0.36, 1] as const };

  const hasSavedAnswers = questions.some((question) => answers[question.id].length > 0);

  useEffect(() => {
    if (!assessmentSessionId || step === "confirmation") return;
    saveAssessmentState({ version: 1, assessmentSessionId, currentStep: step as StoredAssessmentState["currentStep"], answers, reviewReturnState, generatedSnapshot: snapshot });
  }, [answers, assessmentSessionId, reviewReturnState, snapshot, step]);

  useEffect(() => {
    if (typeof step === "number") {
      const question = questions[step];
      trackEvent("friction_question_viewed", { assessment_session_id: assessmentSessionId, question_id: question.id, step_number: step + 1, viewport_category: viewportCategory() });
    }
    if (step === "review") {
      reviewHeading.current?.focus();
      trackEvent("friction_review_viewed", { assessment_session_id: assessmentSessionId });
    }
  }, [assessmentSessionId, step]);

  const begin = (resume: boolean) => {
    if (resume && assessmentSessionId) {
      setStep(0);
      trackEvent("friction_finder_resumed", { assessment_session_id: assessmentSessionId, viewport_category: viewportCategory() });
      return;
    }
    clearAssessmentState();
    const id = newSessionId();
    setAssessmentSessionId(id);
    setAnswers(emptyAnswers());
    setSnapshot(undefined);
    setReviewReturnState(false);
    setStep(0);
    trackEvent("friction_finder_started", { assessment_session_id: id, viewport_category: viewportCategory() });
  };

  const confirmRestart = () => {
    clearAssessmentState();
    trackEvent("friction_finder_restarted", { assessment_session_id: assessmentSessionId });
    setAnswers(emptyAnswers());
    setSnapshot(undefined);
    setAssessmentSessionId("");
    setReviewReturnState(false);
    setStep("landing");
    setRestartOpen(false);
  };

  if (step === "landing") {
    return (
      <div className="page-shell">
        <BrandHeader />
        <m.main className="landing content-shell" initial={reduceMotion ? false : { opacity: 0.78, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={screenTransition}>
          <p className="eyebrow">HuddleCo Friction Finder</p>
          <h1>Find where work is getting harder than it should be.</h1>
          <p className="landing-copy">Answer six focused questions to receive a clear snapshot of the friction, who it affects and why it matters. This is not a diagnosis or recommendation.</p>
          {hasSavedAnswers ? (
            <section className="resume-card" aria-labelledby="resume-heading">
              <h2 id="resume-heading">Continue your Friction Snapshot</h2>
              <div className="button-row">
                <Button onClick={() => begin(true)}>Continue</Button>
                <Button variant="secondary" onClick={() => begin(false)}>Start again</Button>
              </div>
            </section>
          ) : <Button onClick={() => begin(false)}>Find the friction</Button>}
        </m.main>
        <footer className="public-footer"><Link to="/privacy">Privacy</Link></footer>
      </div>
    );
  }

  if (typeof step === "number") {
    const question = questions[step];
    return (
      <div className="page-shell">
        <BrandHeader />
        <QuestionScreen
          question={question}
          questionNumber={step + 1}
          answers={answers}
          onChange={setAnswers}
          onBack={step > 0 ? () => setStep(step - 1) : undefined}
          onContinue={() => {
            trackEvent("friction_question_completed", { assessment_session_id: assessmentSessionId, question_id: question.id, selected_answer_ids: answers[question.id], step_number: step + 1 });
            if (reviewReturnState) {
              setReviewReturnState(false);
              setStep("review");
            } else setStep(step === questions.length - 1 ? "review" : step + 1);
          }}
          onRestart={() => setRestartOpen(true)}
        />
        <RestartDialog open={restartOpen} onCancel={() => setRestartOpen(false)} onConfirm={confirmRestart} />
      </div>
    );
  }

  if (step === "review") {
    return (
      <div className="page-shell">
        <BrandHeader />
        <m.main className="content-shell review-screen" initial={reduceMotion ? false : { opacity: 0.8, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={screenTransition}>
          <h1 ref={reviewHeading} tabIndex={-1}>Review your answers</h1>
          <p className="lead">Check the six answers below. Edit anything before generating your snapshot.</p>
          <div className="review-list">
            {questions.map((question, index) => (
              <m.section
                className="review-item"
                key={question.id}
                aria-labelledby={`review-${question.id}`}
                initial={reduceMotion ? false : { opacity: 0.75, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...screenTransition, delay: reduceMotion ? 0 : index * 0.025 }}
              >
                <div>
                  <h2 id={`review-${question.id}`}>{question.reviewLabel}</h2>
                  <p>{getAnswerLabels(question.id, answers).join(", ")}</p>
                </div>
                <Button variant="text" aria-label={`Edit ${question.reviewLabel}`} onClick={() => {
                  setReviewReturnState(true);
                  setStep(index);
                  trackEvent("friction_answer_edited", { assessment_session_id: assessmentSessionId, question_id: question.id, step_number: index + 1 });
                }}>Edit</Button>
              </m.section>
            ))}
          </div>
          <div className="question-actions">
            <Button variant="secondary" onClick={() => setStep(5)}>Back</Button>
            <Button onClick={() => {
              const generated = generateSnapshot(answers);
              setSnapshot(generated);
              setStep("snapshot");
              trackEvent("friction_snapshot_generated", { assessment_session_id: assessmentSessionId, intent: generated.intent });
              window.setTimeout(() => document.getElementById("snapshot-title")?.focus(), 0);
            }}>Create my Friction Snapshot</Button>
          </div>
          <button className="restart-link" type="button" onClick={() => setRestartOpen(true)}>Start again</button>
        </m.main>
        <RestartDialog open={restartOpen} onCancel={() => setRestartOpen(false)} onConfirm={confirmRestart} />
      </div>
    );
  }

  if (step === "confirmation") {
    return (
      <div className="page-shell">
        <BrandHeader />
        <m.main className="content-shell confirmation-screen" tabIndex={-1} initial={reduceMotion ? false : { opacity: 0.75, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={screenTransition}>
          <m.div className="confirmation-mark" aria-hidden="true" initial={reduceMotion ? false : { scale: 0.94 }} animate={{ scale: 1 }} transition={screenTransition}>✓</m.div>
          <h1>Your conversation request has been sent.</h1>
          <p className="lead">HuddleCo has received your Friction Snapshot and will contact you to arrange the free 20–30 minute conversation.</p>
          <Button onClick={confirmRestart}>Start a new snapshot</Button>
        </m.main>
      </div>
    );
  }

  if (!snapshot) {
    setStep("review");
    return null;
  }

  return (
    <div className="page-shell snapshot-page">
      <BrandHeader />
      <main className="content-shell snapshot-content">
        <SnapshotView snapshot={snapshot} actions={(
          <SnapshotActions
            onPrint={() => { trackEvent("friction_snapshot_printed", { assessment_session_id: assessmentSessionId, intent: snapshot.intent }); window.print(); }}
            onSave={() => downloadSnapshotPdf(snapshot)}
          />
        )} />
        <ContactForm answers={answers} snapshot={snapshot} assessmentSessionId={assessmentSessionId} onSuccess={() => {
          clearAssessmentState();
          setStep("confirmation");
          window.setTimeout(() => document.querySelector<HTMLElement>(".confirmation-screen")?.focus(), 0);
        }} />
        <button className="restart-link no-print" type="button" onClick={() => setRestartOpen(true)}>Start again</button>
      </main>
      <RestartDialog open={restartOpen} onCancel={() => setRestartOpen(false)} onConfirm={confirmRestart} />
    </div>
  );
}
